import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, CartesianGrid,
} from "recharts";
import {
  XIcon,
  RocketLaunchIcon,
  StarIcon,
  TrophyIcon,
  ChartLineUpIcon,
  CalendarIcon,
} from "@phosphor-icons/react";

/**
 * Full-detail modal for a single MT5 bot. Fetches /mt5admin/bot/{name} for
 * per-symbol peer stats + prior backtest events, then renders every metric
 * with charts comparing the bot against its symbol median.
 */
const BACKTEST_DEPOSIT = 2000; // Run_MT5_Brain.ps1 tester ini Deposit=2000


export default function BotDetailModal({ botName, onClose, onUse }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [enqueuing, setEnqueuing] = useState(false);
  const [enqueueMsg, setEnqueueMsg] = useState(null);

  useEffect(() => {
    if (!botName) return;
    setLoading(true);
    setErr(null);
    setEnqueueMsg(null);
    api.get(`/mt5admin/bot/${encodeURIComponent(botName)}`)
      .then((r) => { setDetail(r.data); setLoading(false); })
      .catch((e) => { setErr(e?.response?.data?.detail || "failed to load"); setLoading(false); });
  }, [botName]);

  const bot   = detail?.bot ?? {};
  const peer  = detail?.peer_stats ?? {};
  const hist  = detail?.history ?? [];
  const rank  = detail?.peer_rank;
  const tick  = bot?.tick_validation ?? null;
  const tickStats = tick?.tick_stats ?? null;
  const barStats  = tick?.bars_stats ?? null;
  // v1.9.11: bots produced by the tick-only brain (test_model="real_ticks")
  // are truthful from birth - no separate validation record needed. Detect
  // that path and drive the chip + verdict independently.
  const isTickNative = bot?.test_model === "real_ticks";
  const chipState = tick?.status
    ? tick.status                    // "passed" / "failed" / "pending"
    : (isTickNative ? "native_tick" : "bars_only");

  const enqueueTickValidation = async () => {
    if (!bot.bot_name) return;
    setEnqueuing(true);
    setEnqueueMsg(null);
    try {
      const r = await api.post(`/mt5admin/validate/enqueue/${encodeURIComponent(bot.bot_name)}`);
      setEnqueueMsg(r.data?.already ? "Already in queue" : "Queued for real-tick validation");
    } catch (e) {
      setEnqueueMsg(e?.response?.data?.detail || "Enqueue failed");
    } finally {
      setEnqueuing(false);
    }
  };

  // v1.9.10: per-month breakdown from the parser. Hooks must run every
  // render, so this sits ABOVE the early-return for botName.
  const monthly = useMemo(() => {
    const raw = Array.isArray(bot.monthly_breakdown) ? bot.monthly_breakdown : [];
    return raw
      .map((m) => ({ ...m, month: (m.month || "").replace(".", "-") }))
      .sort((a, b) => (a.month < b.month ? -1 : 1));
  }, [bot.monthly_breakdown]);

  // Older reports cut balances at the thousands space ("27 928" -> 27), so trust
  // end_equity only when the final value matches deposit + net profit.
  const equityLine = useMemo(() => {
    const last = monthly.length ? Number(monthly[monthly.length - 1].end_equity ?? 0) : 0;
    const expected = BACKTEST_DEPOSIT + Number(bot.net_profit ?? 0);
    const trustReported = last > 0 && Math.abs(last - expected) <= 0.1 * expected;
    let running = BACKTEST_DEPOSIT;
    return monthly.map((m) => {
      running += Number(m.gross_pnl ?? 0);
      const eq = trustReported ? Number(m.end_equity ?? running) : running;
      return { month: m.month, equity: Number(eq.toFixed(2)) };
    });
  }, [monthly, bot.net_profit]);
  const equityByMonth = useMemo(
    () => Object.fromEntries(equityLine.map((e) => [e.month, e.equity])), [equityLine]);

  // v1.11 - identify best/worst monthly cells for highlighting
  const monthlyExtremes = useMemo(() => {
    if (!monthly.length) return { bestPnl: null, worstPnl: null, bestWinRate: null };
    let bestPnl = monthly[0], worstPnl = monthly[0], bestWR = monthly[0];
    for (const m of monthly) {
      if (Number(m.gross_pnl ?? 0) > Number(bestPnl.gross_pnl ?? 0)) bestPnl = m;
      if (Number(m.gross_pnl ?? 0) < Number(worstPnl.gross_pnl ?? 0)) worstPnl = m;
      if (Number(m.win_rate ?? -1) > Number(bestWR.win_rate ?? -1)) bestWR = m;
    }
    return { bestPnl, worstPnl, bestWinRate: bestWR };
  }, [monthly]);

  if (!botName) return null;

  const compareBars = [
    { metric: "PF",     bot: bot.profit_factor ?? 0,   peer: peer.median_profit_factor ?? 0 },
    { metric: "Sharpe", bot: bot.sharpe_ratio ?? 0,    peer: peer.median_sharpe ?? 0 },
    { metric: "DD %",   bot: bot.max_dd_pct ?? 0,      peer: peer.median_max_dd ?? 0 },
    { metric: "PnL/1k", bot: (bot.net_profit ?? 0)/1000, peer: (peer.median_net_profit ?? 0)/1000 },
  ];

  const historyLine = hist.map((h, i) => ({
    idx: i + 1,
    pnl: h.net_profit ?? 0,
    pf:  h.profit_factor ?? 0,
    ts:  h.ts ? new Date(h.ts).toLocaleDateString() : "",
  }));

  const monthlyPnlBars = monthly.map((m) => ({
    month:     m.month,
    gross_pnl: Number(m.gross_pnl ?? 0),
  }));

  // 24-month grid: profit_months mark as green from start, losing_months as red at end,
  // remainder grey. Best we can do without per-month PnL granularity in DB.
  const monthTiles = [];
  const totalMonths = monthly.length || bot.backtest_months || 24;
  let profM = bot.profit_months || 0;
  let lossM = bot.losing_months || 0;
  if (monthly.length) {
    profM = 0; lossM = 0;
    for (const m of monthly) {
      const v = Number(m.gross_pnl ?? 0);
      if (v > 0.01) profM++; else if (v < -0.01) lossM++;
      monthTiles.push({ colour: v > 0.01 ? "#2ECA7F" : v < -0.01 ? "#F87171" : "#334155", label: m.month });
    }
  } else {
    for (let i = 0; i < totalMonths; i++) {
      const colour = i < profM ? "#2ECA7F" : i >= totalMonths - lossM ? "#F87171" : "#334155";
      monthTiles.push({ colour, label: String(i + 1) });
    }
  }

  return (
    <div data-testid="bot-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="relative w-full max-w-5xl my-8 rounded-lg border border-[#D6FF38]/30 bg-[#050914] shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <button data-testid="bot-detail-close" onClick={onClose}
          className="absolute top-3 right-3 z-10 text-[#64748B] hover:text-[#F1F5F9]">
          <XIcon size={20} />
        </button>

        {/* ---- header ---- */}
        <div className="p-6 border-b border-[#D6FF38]/20">
          <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38] mb-2 flex items-center gap-2">
            <ChartLineUpIcon size={12} weight="fill" /> Bot detail
            {bot.is_champion && (<span className="ml-2 text-[#FBBF24] flex items-center gap-1"><StarIcon size={10} weight="fill" /> CHAMPION</span>)}
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="mono text-lg font-medium text-[#F1F5F9] flex items-center gap-2">
                {bot.bot_name}
                {chipState === "passed" && (
                  <span data-testid="tick-badge-passed" className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#2ECA7F20", color: "#2ECA7F", border: "1px solid #2ECA7F60" }}>Tick-passed</span>
                )}
                {chipState === "failed" && (
                  <span data-testid="tick-badge-failed" className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#F8717120", color: "#F87171", border: "1px solid #F8717160" }}>Tick-failed</span>
                )}
                {(chipState === "pending" || chipState === "picked_up") && (
                  <span data-testid="tick-badge-pending" className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#FBBF2420", color: "#FBBF24", border: "1px solid #FBBF2460" }}>Tick-queued</span>
                )}
                {chipState === "native_tick" && (
                  <span data-testid="tick-badge-native" className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#2ECA7F20", color: "#2ECA7F", border: "1px solid #2ECA7F60" }}>Real-tick</span>
                )}
                {chipState === "bars_only" && (
                  <span data-testid="tick-badge-none" className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#33415520", color: "#94A3B8", border: "1px solid #33415580" }}>Bars only</span>
                )}
              </div>
              <div className="mono text-[11px] text-[#94A3B8] mt-1 flex items-center gap-3 flex-wrap">
                <span>{bot.symbol} · {bot.period || "M15"}</span>
                {bot.backtest_from && bot.backtest_to && (
                  <span className="flex items-center gap-1"><CalendarIcon size={11} /> {bot.backtest_from} → {bot.backtest_to}</span>
                )}
                {rank && peer.count > 0 && (
                  <span className="flex items-center gap-1 text-[#FBBF24]"><TrophyIcon size={11} weight="fill" /> Rank #{rank} of {peer.count} {bot.symbol} 24-mo bots</span>
                )}
              </div>
            </div>
            {onUse && (
              <div className="flex flex-col md:flex-row gap-2">
                {!isTickNative && (
                  <button data-testid="bot-detail-validate"
                    onClick={enqueueTickValidation}
                    disabled={enqueuing || tick?.status === "pending" || tick?.status === "picked_up"}
                    className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-md border transition disabled:opacity-40"
                    style={{ borderColor: "#E879F9", color: "#E879F9", background: "#E879F910" }}>
                    {enqueuing ? "…" : tick?.status === "pending" || tick?.status === "picked_up" ? "In tick queue" : "Validate on real ticks"}
                  </button>
                )}
                <button data-testid="bot-detail-use"
                  onClick={() => { onUse(bot); onClose(); }}
                  className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-md hover:scale-[1.03] transition"
                  style={{ background: "#D6FF38", color: "#050914" }}>
                  <RocketLaunchIcon size={12} weight="fill" className="inline mr-1" /> Use this bot
                </button>
              </div>
            )}
          </div>
          {enqueueMsg && (
            <div data-testid="tick-enqueue-msg" className="mt-3 mono text-[10px] text-[#E879F9]">{enqueueMsg}</div>
          )}
        </div>

        {loading && (<div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>)}
        {err && (<div className="p-8 text-center mono text-xs text-[#F87171]">{err}</div>)}

        {!loading && !err && (
          <div className="p-6 space-y-6">

            {/* ---- headline stats ---- */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Net PnL"    value={`£${(bot.net_profit ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color={(bot.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171"} />
              <Stat label="Profit Factor" value={(bot.profit_factor ?? 0).toFixed(2)} color={(bot.profit_factor ?? 0) >= 1.5 ? "#D6FF38" : (bot.profit_factor ?? 0) >= 1.2 ? "#22D3EE" : "#94A3B8"} />
              <Stat label="Max DD %"   value={`${(bot.max_dd_pct ?? 0).toFixed(2)}%`} color={(bot.max_dd_pct ?? 0) < 15 ? "#2ECA7F" : (bot.max_dd_pct ?? 0) < 25 ? "#FBBF24" : "#F87171"} />
              <Stat label="Sharpe"     value={(bot.sharpe_ratio ?? 0).toFixed(2)} color={(bot.sharpe_ratio ?? 0) >= 2 ? "#D6FF38" : (bot.sharpe_ratio ?? 0) >= 1 ? "#22D3EE" : "#94A3B8"} />
              <Stat label="Total Trades" value={(bot.total_trades ?? 0).toLocaleString()} />
              <Stat label="Avg Trade"    value={`£${(bot.avg_trade ?? bot.expected_payoff ?? 0).toFixed(2)}`} color={(bot.avg_trade ?? bot.expected_payoff ?? 0) > 0 ? "#2ECA7F" : "#F87171"} />
              <Stat label="Recovery"     value={(bot.recovery_factor ?? 0).toFixed(2)} />
              <Stat label="Max Consec Loss" value={bot.max_consec_losses ?? 0} color={(bot.max_consec_losses ?? 0) <= 5 ? "#2ECA7F" : (bot.max_consec_losses ?? 0) <= 10 ? "#FBBF24" : "#F87171"} />
            </div>

            {/* ---- month grid + comparison ---- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-[#1F2937] rounded-md p-4 bg-[#0B0D14]">
                <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-3">
                  Monthly outcome · {totalMonths} months
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {monthTiles.map((t, i) => (
                    <div key={i} data-testid={`month-tile-${i}`} className="aspect-square rounded flex items-center justify-center mono text-[8px] text-[#050914]"
                         style={{ background: t.colour }}
                         title={`${t.label}: ${t.colour === "#2ECA7F" ? "profitable" : t.colour === "#F87171" ? "losing" : "flat/unknown"}`}>
                      {t.label.length > 2 ? t.label.slice(2).replace("-", "/") : t.label}
                    </div>
                  ))}
                </div>
                <div className="mt-3 mono text-[10px] flex items-center gap-4">
                  <span data-testid="profit-months-count" className="text-[#2ECA7F]">■ {profM} profit</span>
                  <span data-testid="losing-months-count" className="text-[#F87171]">■ {lossM} losing</span>
                  <span className="text-[#334155]">■ {totalMonths - profM - lossM} flat/unknown</span>
                </div>
              </div>

              <div className="border border-[#1F2937] rounded-md p-4 bg-[#0B0D14]">
                <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-3">
                  This bot vs {bot.symbol} median · {peer.count ?? 0} peers
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={compareBars} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="metric" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#050914", border: "1px solid #1F2937", fontSize: 11 }} />
                    <Bar dataKey="peer" fill="#334155" name="peer median" />
                    <Bar dataKey="bot"  fill="#D6FF38" name="this bot" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ---- backtest history line ---- */}
            {historyLine.length > 1 && (
              <div className="border border-[#1F2937] rounded-md p-4 bg-[#0B0D14]">
                <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-3">
                  Backtest history · {historyLine.length} events
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={historyLine} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="idx" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#050914", border: "1px solid #1F2937", fontSize: 11 }} />
                    <Line type="monotone" dataKey="pnl" stroke="#D6FF38" strokeWidth={2} dot={false} name="PnL" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ---- REAL-TICK VALIDATION (bars vs ticks side-by-side) ---- */}
            {tick && (
              <div data-testid="tick-validation-section" className="border rounded-md p-4 bg-[#0B0D14]"
                style={{ borderColor: tick.status === "passed" ? "#2ECA7F60" : tick.status === "failed" ? "#F8717160" : "#FBBF2460" }}>
                <div className="mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-2"
                  style={{ color: tick.status === "passed" ? "#2ECA7F" : tick.status === "failed" ? "#F87171" : "#FBBF24" }}>
                  <ChartLineUpIcon size={12} weight="fill" />
                  Real-tick validation · {tick.status?.toUpperCase() || "PENDING"}
                </div>
                {tickStats ? (
                  <div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[10px] mono">
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Net PnL</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span style={{ color: (barStats?.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>£{Number(barStats?.net_profit ?? 0).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span style={{ color: (tickStats.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>£{Number(tickStats.net_profit ?? 0).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                        </div>
                      </div>
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Profit factor</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span className="text-[#F1F5F9]">{Number(barStats?.profit_factor ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span style={{ color: (tickStats.profit_factor ?? 0) >= 1.2 ? "#D6FF38" : "#F87171" }}>{Number(tickStats.profit_factor ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Max DD %</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span className="text-[#F1F5F9]">{Number(barStats?.max_dd_pct ?? 0).toFixed(1)}%</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span style={{ color: (tickStats.max_dd_pct ?? 0) < 15 ? "#2ECA7F" : (tickStats.max_dd_pct ?? 0) < 25 ? "#FBBF24" : "#F87171" }}>{Number(tickStats.max_dd_pct ?? 0).toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Trades</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span className="text-[#F1F5F9]">{barStats?.total_trades ?? 0}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span className="text-[#F1F5F9]">{tickStats.total_trades ?? 0}</span>
                        </div>
                      </div>
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Win rate</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span className="text-[#F1F5F9]">{Number(barStats?.win_rate ?? 0).toFixed(1)}%</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span className="text-[#F1F5F9]">{Number(tickStats.win_rate ?? 0).toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="border border-[#1F2937] rounded p-2">
                        <div className="text-[#64748B] uppercase tracking-widest mb-1">Sharpe</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#94A3B8]">bars</span>
                          <span className="text-[#F1F5F9]">{Number(barStats?.sharpe_ratio ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[#E879F9]">ticks</span>
                          <span className="text-[#F1F5F9]">{Number(tickStats.sharpe_ratio ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 mono text-[10px] leading-relaxed" style={{ color: tick.status === "passed" ? "#2ECA7F" : "#F87171" }}>
                      {tick.status === "passed"
                        ? "Verdict: SURVIVED real ticks. This bot is store-eligible."
                        : "Verdict: FAILED on real ticks. Bar-model result was misleading — do not deploy this bot live."}
                    </div>
                  </div>
                ) : (
                  <div className="mono text-[10px] text-[#FBBF24]">
                    Queued for tick validation. Backtest runs on the VPS (15–60 min each). Check back after your validator loop processes this bot.
                  </div>
                )}
              </div>
            )}

            {/* ---- monthly performance ---- */}            {monthly.length > 0 && (
              <div data-testid="monthly-performance" className="border border-[#D6FF38]/30 rounded-md p-4 bg-[#0B0D14]">
                <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38] mb-3 flex items-center gap-2">
                  <CalendarIcon size={12} weight="fill" /> Monthly performance · {monthly.length} months
                </div>

                {/* PnL bar chart */}
                <div className="mb-4">
                  <div className="mono text-[9px] uppercase tracking-widest text-[#64748B] mb-2">Gross PnL by month</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={monthlyPnlBars} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                      <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 9 }} interval={monthly.length > 12 ? 1 : 0} angle={-40} textAnchor="end" height={45} />
                      <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#050914", border: "1px solid #1F2937", fontSize: 11 }} formatter={(v) => [`£${Number(v).toLocaleString(undefined,{maximumFractionDigits:2})}`, "PnL"]} />
                      <Bar dataKey="gross_pnl" name="PnL">
                        {monthlyPnlBars.map((d, i) => (
                          <Cell key={i} fill={d.gross_pnl >= 0 ? "#2ECA7F" : "#F87171"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Running equity line */}
                {equityLine.length > 1 && (
                  <div className="mb-4">
                    <div className="mono text-[9px] uppercase tracking-widest text-[#64748B] mb-2">Running equity (month-end)</div>
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={equityLine} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                        <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 9 }} interval={monthly.length > 12 ? 1 : 0} angle={-40} textAnchor="end" height={45} />
                        <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#050914", border: "1px solid #1F2937", fontSize: 11 }} formatter={(v) => [`£${Number(v).toLocaleString(undefined,{maximumFractionDigits:2})}`, "Equity"]} />
                        <Line type="monotone" dataKey="equity" stroke="#22D3EE" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Table */}
                <div className="overflow-x-auto">
                  <table data-testid="monthly-breakdown-table" className="w-full mono text-[10px] whitespace-nowrap">
                    <thead>
                      <tr className="text-[#64748B] uppercase tracking-widest border-b border-[#1F2937]">
                        <th className="text-left py-2 pr-2">Month</th>
                        <th className="text-right py-2 px-2">Trades</th>
                        <th className="text-right py-2 px-2">W</th>
                        <th className="text-right py-2 px-2">L</th>
                        <th className="text-right py-2 px-2">Win %</th>
                        <th className="text-right py-2 px-2">PnL</th>
                        <th className="hidden md:table-cell text-right py-2 px-2">Best</th>
                        <th className="hidden md:table-cell text-right py-2 px-2">Worst</th>
                        <th className="text-right py-2 pl-2">Equity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.map((m) => {
                        const pnl = Number(m.gross_pnl ?? 0);
                        const isBestPnl  = monthlyExtremes.bestPnl  && m.month === monthlyExtremes.bestPnl.month  && pnl > 0;
                        const isWorstPnl = monthlyExtremes.worstPnl && m.month === monthlyExtremes.worstPnl.month && pnl < 0;
                        const isBestWR   = monthlyExtremes.bestWinRate && m.month === monthlyExtremes.bestWinRate.month && m.win_rate != null;
                        return (
                          <tr key={m.month}
                            className={`border-b border-[#111827] hover:bg-[#111827] ${isBestPnl ? "bg-[#2ECA7F]/[0.08] ring-1 ring-inset ring-[#2ECA7F]/40" : ""} ${isWorstPnl ? "bg-[#F87171]/[0.08] ring-1 ring-inset ring-[#F87171]/40" : ""}`}
                            data-testid={`monthly-row-${m.month}${isBestPnl ? "-best" : ""}${isWorstPnl ? "-worst" : ""}`}>
                            <td className="py-2 pr-2 text-[#F1F5F9]">
                              {isBestPnl && <span className="mr-1 text-[#2ECA7F]" title="Best month">★</span>}
                              {isWorstPnl && <span className="mr-1 text-[#F87171]" title="Worst month">▼</span>}
                              {m.month}
                            </td>
                            <td className="text-right py-2 px-2 text-[#F1F5F9]">{m.trades ?? "—"}</td>
                            <td className="text-right py-2 px-2 text-[#2ECA7F]">{m.wins ?? "—"}</td>
                            <td className="text-right py-2 px-2 text-[#F87171]">{m.losses ?? "—"}</td>
                            <td className={`text-right py-2 px-2 ${isBestWR ? "font-medium text-[#D6FF38]" : "text-[#94A3B8]"}`} data-testid={isBestWR ? "best-winrate-cell" : undefined}>
                              {m.win_rate != null ? `${Number(m.win_rate).toFixed(1)}%${isBestWR ? " ★" : ""}` : "—"}
                            </td>
                            <td className="text-right py-2 px-2 font-medium" style={{ color: pnl >= 0 ? "#2ECA7F" : "#F87171" }}>
                              £{pnl.toLocaleString(undefined,{maximumFractionDigits:2})}
                            </td>
                            <td className="hidden md:table-cell text-right py-2 px-2 text-[#2ECA7F]">{m.best_trade != null ? `£${Number(m.best_trade).toLocaleString(undefined,{maximumFractionDigits:2})}` : "—"}</td>
                            <td className="hidden md:table-cell text-right py-2 px-2 text-[#F87171]">{m.worst_trade != null ? `£${Number(m.worst_trade).toLocaleString(undefined,{maximumFractionDigits:2})}` : "—"}</td>
                            <td data-testid={`monthly-equity-${m.month}`} className="text-right py-2 pl-2 text-[#22D3EE]">{equityByMonth[m.month] != null ? `£${Number(equityByMonth[m.month]).toLocaleString(undefined,{maximumFractionDigits:0})}` : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {monthly.some((m) => m.trades == null) && (
                  <div className="mt-3 mono text-[9px] text-[#94A3B8] italic">
                    Note: rows with — for trades/wins/losses were parsed with the legacy aggregator.
                    Rerun the latest Reparse_Reports.ps1 (v2.2+) to fill per-trade detail from the Deals table.
                  </div>
                )}
              </div>
            )}

            {/* ---- peer context footer ---- */}
            {peer.best_bot_name && peer.best_bot_name !== bot.bot_name && (
              <div className="mono text-[10px] text-[#64748B] leading-relaxed border-t border-[#1F2937] pt-4">
                Best {bot.symbol} bot overall: <span className="text-[#D6FF38]">{peer.best_bot_name}</span>
                {' · '}£{(peer.best_net_profit ?? 0).toLocaleString(undefined,{maximumFractionDigits:0})} PnL
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-[#1F2937] rounded-md p-3 bg-[#0B0D14]">
      <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className="mono text-lg mt-1 font-medium" style={{ color: color || "#F1F5F9" }}>{value}</div>
    </div>
  );
}
